"use client";

import { useId, useState } from "react";
import { asText, isFilled } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";

const titleComponents = {
  heading2: ({ children }) => <h2 className="h2-n lg:dk text-bleu">{children}</h2>,
  heading3: ({ children }) => <h3 className="h2-n lg:dk text-bleu">{children}</h3>,
};

const answerComponents = {
  paragraph: ({ children }) => (
    <p className="p-base-n text-bleu-90 not-last:mb-5">{children}</p>
  ),
  strong: ({ children }) => <strong className="md">{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  hyperlink: ({ children, node }) => (
    <a href={node.data.url} className="underline underline-offset-2">
      {children}
    </a>
  ),
  listItem: ({ children }) => <li className="p-base-n text-bleu-90">{children}</li>,
};

// Full class strings so Tailwind can detect them.
const variants = {
  default: {
    section: "bg-beige-30",
    list: "gap-5",
    item: "",
    open: "bg-blanc",
    closed: "bg-beige-30",
    question: "",
  },
  light: {
    section: "bg-blanc",
    list: "gap-5",
    item: "",
    open: "bg-bleu-5",
    closed: "bg-blanc",
    question: "",
  },
  faqGuide: {
    section: "bg-blanc",
    list: "gap-6",
    item: "border border-vert-10",
    open: "bg-vert-10",
    closed: "bg-blanc",
    question: "max-w-[600px]",
  },
};

/**
 * Cpnt - Question: accordion item. Only one item is open at a time.
 * @param {{ item: { question: import("@prismicio/client").RichTextField, answer: import("@prismicio/client").RichTextField }, styles: typeof variants.default, isOpen: boolean, onToggle: () => void, baseId: string, index: number }} props
 */
const Question = ({ item, styles, isOpen, onToggle, baseId, index }) => {
  const questionId = `${baseId}-question-${index}`;
  const answerId = `${baseId}-answer-${index}`;
  const hasAnswer = isFilled.richText(item.answer);

  return (
    <li
      className={`overflow-hidden rounded-[20px] px-5 py-[30px] transition-colors duration-300 lg:px-10 ${styles.item} ${isOpen ? styles.open : styles.closed}`}
    >
      <h3>
        <button
          type="button"
          id={questionId}
          aria-expanded={isOpen}
          aria-controls={hasAnswer ? answerId : undefined}
          onClick={onToggle}
          className={`w-full cursor-pointer text-left p-lg-n sbd text-bleu focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-bleu ${styles.question}`}
        >
          {asText(item.question)}
        </button>
      </h3>

      {hasAnswer && (
        <div
          id={answerId}
          role="region"
          aria-labelledby={questionId}
          inert={!isOpen}
          className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
        >
          <div className="overflow-hidden">
            <div className="pt-7">
              <PrismicRichText field={item.answer} components={answerComponents} />
            </div>
          </div>
        </div>
      )}
    </li>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.FaqSlice} FaqSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<FaqSlice>} FaqProps
 * @type {import("react").FC<FaqProps>}
 */
const Faq = ({ slice }) => {
  const { sub_header, title, questions } = slice.primary;
  const styles = variants[slice.variation] ?? variants.default;
  const baseId = useId();
  const [openIndex, setOpenIndex] = useState(0);

  const filledQuestions = Array.isArray(questions)
    ? questions.filter(
        (item) => isFilled.richText(item.question) || isFilled.richText(item.answer),
      )
    : [];

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`contre-uno padding-x padding-y-md ${styles.section}`}
    >
      <div className="framing-sm flex flex-col gap-[60px]">
        {(isFilled.keyText(sub_header) || isFilled.richText(title)) && (
          <div className="flex w-full flex-col gap-1.5">
            {isFilled.keyText(sub_header) && (
              <p className="section-text-n text-rouge">{sub_header}</p>
            )}
            {isFilled.richText(title) && (
              <PrismicRichText field={title} components={titleComponents} />
            )}
          </div>
        )}

        {filledQuestions.length > 0 && (
          <ul className={`flex flex-col ${styles.list}`}>
            {filledQuestions.map((item, index) => (
              <Question
                key={`question-${index}`}
                item={item}
                styles={styles}
                isOpen={openIndex === index}
                onToggle={() => setOpenIndex(openIndex === index ? null : index)}
                baseId={baseId}
                index={index}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default Faq;
